import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { formatDateTime } from '../utils/dateFormat';
import {
  FileText,
  Trash2,
  XCircle,
  Calendar,
  Save,
  Link,
  Mail,
  Loader2,
} from 'lucide-react';
import DocumentUpload from '../components/DocumentUpload';
import { netopiaService } from '../api/netopiaService';
import { netopiaService as netopiaDiscountService } from '../api/netopiaService-discount';
import { fadeInUp, staggerContainer } from '../utils/animations';

interface Profile {
  id: string;
  role: string;
  prenume: string;
  nume: string;
  email: string;
  consultation_status:
    | 'not_requested'
    | 'requested'
    | 'planned'
    | 'closed'
    | 'paid'
    | 'contacted';
  planned_date?: string;
  meeting_url?: string;
  cnp?: string;
  serviciu?: 'standard' | 'asociatie' | 'probono';
  telefon?: string;
  drive_link?: string;
}

interface FileObject {
  name: string;
  id: string;
  created_at: string;
}

interface PaymentNotification {
  message: string;
  type: 'success' | 'error';
}

export default function Patient() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState<FileObject[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standardPaymentLoading, setStandardPaymentLoading] = useState(false);
  const [asociatiePaymentLoading, setAsociatiePaymentLoading] = useState(false);
  const [paymentNotification, setPaymentNotification] =
    useState<PaymentNotification | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [requestingConsultation, setRequestingConsultation] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [resendingLetter, setResendingLetter] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const handleDownload = async (fileName: string) => {
    if (!profile?.cnp) return;

    try {
      const { data, error } = await supabase.storage
        .from('medical-documents')
        .download(`${profile.cnp}/${fileName}`);

      if (error) throw error;

      // Create a URL for the downloaded file
      const url = URL.createObjectURL(data);

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Eroare la descărcarea documentului');
      setTimeout(() => setError(null), 3000);
    }
  };

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!profile || profile.role !== 'pacient') {
        navigate('/');
        return;
      }

      setUser(user);
      setProfile(profile);

      if (profile.cnp) {
        await fetchFiles(profile.cnp);
        await fetchMedicalLetter(user.id);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const fetchFiles = async (cnp: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('medical-documents')
        .list(cnp);

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchMedicalLetter = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('medical_letters')
        .select('notes4doctor')
        .eq('patient_id', patientId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching medical letter:', error);
        return;
      }

      if (data?.notes4doctor) {
        setNotes(data.notes4doctor);
      }
    } catch (error) {
      console.error('Error fetching medical letter:', error);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!profile?.cnp) return;

    try {
      const { error } = await supabase.storage
        .from('medical-documents')
        .remove([`${profile.cnp}/${fileName}`]);

      if (error) throw error;

      await fetchFiles(profile.cnp);
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleSaveNotes = async () => {
    if (!user) return;
    setSavingNotes(true);

    try {
      const { error } = await supabase.from('medical_letters').upsert(
        {
          patient_id: user.id,
          notes4doctor: notes,
        },
        {
          onConflict: 'patient_id',
        }
      );

      if (error) throw error;

      setPaymentNotification({
        message: 'Notele au fost salvate cu succes',
        type: 'success',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    } catch (error) {
      console.error('Error saving notes:', error);
      setPaymentNotification({
        message: 'Eroare la salvarea notelor',
        type: 'error',
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const handleRequestConsultation = async () => {
    if (!profile || requestingConsultation) return;
    setRequestingConsultation(true);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/solicitare-programare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: profile.prenume,
            nume: profile.nume,
            email: profile.email,
            cnp: profile.cnp,
            serviciu: profile.serviciu,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send webhook');
      }

      const { error } = await supabase
        .from('profiles')
        .update({ consultation_status: 'requested' })
        .eq('id', profile.id);

      if (error) throw error;

      await checkAuth();

      setPaymentNotification({
        message: 'Cererea de programare a fost trimisă cu succes',
        type: 'success',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    } catch (error) {
      console.error('Error requesting consultation:', error);
      setPaymentNotification({
        message: 'Eroare la trimiterea cererii',
        type: 'error',
      });
    } finally {
      setRequestingConsultation(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!profile || cancellingAppointment) return;
    setCancellingAppointment(true);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/anulare-programare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: profile.prenume,
            nume: profile.nume,
            email: profile.email,
            cnp: profile.cnp,
            serviciu: profile.serviciu,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send webhook');
      }

      const newStatus = profile.serviciu === 'probono' ? 'not_requested' : 'paid';
      const { error } = await supabase
        .from('profiles')
        .update({
          consultation_status: newStatus,
          planned_date: null,
          meeting_url: null,
        })
        .eq('id', profile.id);

      if (error) throw error;

      await checkAuth();

      setPaymentNotification({
        message: 'Programarea a fost anulată cu succes',
        type: 'success',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setPaymentNotification({
        message: 'Eroare la anularea programării',
        type: 'error',
      });
    } finally {
      setCancellingAppointment(false);
    }
  };

  const handleGenerateUploadLink = async () => {
    if (!profile || generatingLink) return;
    setGeneratingLink(true);
    setError(null);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/link-drive',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: profile.prenume,
            nume: profile.nume,
            cnp: profile.cnp,
          }),
        }
      );

      const link = await response.text();

      if (link && link.startsWith('http')) {
        // Update the drive_link in the database
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ drive_link: link })
          .eq('id', profile.id);

        if (updateError) throw updateError;

        // Refresh profile data
        await checkAuth();

        setPaymentNotification({
          message: 'Link generat cu succes',
          type: 'success',
        });
        setTimeout(() => setPaymentNotification(null), 3000);
      } else {
        throw new Error('Link invalid primit de la server');
      }
    } catch (error) {
      console.error('Error generating upload link:', error);
      setPaymentNotification({
        message: 'Eroare la generarea link-ului. Vă rugăm încercați din nou.',
        type: 'error',
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleResendLetter = async () => {
    if (!profile || resendingLetter) return;
    setResendingLetter(true);

    try {
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/retrimite-scrisoare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prenume: profile.prenume,
            nume: profile.nume,
            cnp: profile.cnp,
            email: profile.email,
          }),
        }
      );

      const result = await response.text();

      if (result === 'Trimis') {
        setPaymentNotification({
          message:
            'Scrisoarea medicală a fost retrimisă pe adresa dumneavoastră de e-mail',
          type: 'success',
        });
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      setPaymentNotification({
        message:
          'A apărut o eroare. Vă rog să contactați echipa de suport la admin@decizieoncologica.ro',
        type: 'error',
      });
    } finally {
      setResendingLetter(false);
      setTimeout(() => setPaymentNotification(null), 5000);
    }
  };

  const handleNewConsultationRequest = async () => {
    if (!profile) return;

    try {
      // Start a batch of updates
      const updates = [];

      // 1. Update profile
      updates.push(
        supabase
          .from('profiles')
          .update({
            consultation_status: 'not_requested',
            serviciu: 'standard',
            planned_date: null,
            meeting_url: null,
            drive_link: null,
          })
          .eq('id', profile.id)
      );

      // 2. Update medical letter
      updates.push(
        supabase
          .from('medical_letters')
          .update({
            closed4editing: null,
            signed_by: null,
          })
          .eq('patient_id', profile.id)
      );

      // Execute all updates
      const [profileResult, letterResult] = await Promise.all(updates);

      // Check for errors
      if (profileResult.error) throw profileResult.error;
      if (letterResult.error && letterResult.error.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is fine if there's no letter
        throw letterResult.error;
      }

      // Refresh the profile data
      await checkAuth();

      setPaymentNotification({
        message: 'Status actualizat cu succes',
        type: 'success',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    } catch (error) {
      console.error('Error updating status:', error);
      setPaymentNotification({
        message: 'Eroare la actualizarea statusului',
        type: 'error',
      });
    }
  };

  const handleNetopiaPayment = async () => {
    if (!profile || !user || standardPaymentLoading) return;

    setStandardPaymentLoading(true);
    setPaymentNotification(null);

    try {
      // First update service type in database
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ serviciu: 'standard' })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Determine which service to use based on coupon code
      const discountCodes = ['navicare', 'oamenibuni'];
      const service = discountCodes.includes(couponCode.toLowerCase())
        ? netopiaDiscountService
        : netopiaService;

      // Determine discount code to store
      let discountCodeToStore = 'none';
      if (couponCode.toLowerCase() === 'navicare') {
        discountCodeToStore = 'navicare';
      } else if (couponCode.toLowerCase() === 'oamenibuni') {
        discountCodeToStore = 'oamenibuni';
      }

      // Create payment through Netopia
      const paymentUrl = await service.createPayment(
        user.id,
        profile.email,
        `${profile.prenume} ${profile.nume}`,
        discountCodeToStore
      );

      // Redirect to Netopia payment page
      window.location.href = paymentUrl;
    } catch (error) {
      console.error('Error initiating payment:', error);
      setPaymentNotification({
        message: 'Eroare la inițierea plății. Vă rugăm să încercați din nou.',
        type: 'error',
      });
      setStandardPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Se încarcă...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">Eroare la încărcarea profilului</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div className="mb-8" variants={fadeInUp}>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bun venit, {profile.prenume} {profile.nume}
            </h1>
            <p className="text-gray-600">
              Aici puteți gestiona documentele medicale și programările
              dumneavoastră.
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* Status Section */}
            <motion.div
              className="bg-gray-50 p-6 rounded-lg"
              variants={fadeInUp}
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Status Consultație
              </h2>
              <div className="space-y-2">
                <p className="text-gray-700">
                  Status actual:{' '}
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      profile.consultation_status === 'not_requested'
                        ? 'bg-gray-100 text-gray-800'
                        : profile.consultation_status === 'requested'
                        ? 'bg-yellow-100 text-yellow-800'
                        : profile.consultation_status === 'contacted'
                        ? 'bg-yellow-100 text-yellow-800'
                        : profile.consultation_status === 'planned'
                        ? 'bg-blue-100 text-blue-800'
                        : profile.consultation_status === 'closed'
                        ? 'bg-green-100 text-green-800'
                        : profile.consultation_status === 'paid'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {profile.consultation_status === 'not_requested'
                      ? 'Fără cerere'
                      : profile.consultation_status === 'requested'
                      ? 'Cerere în așteptare'
                      : profile.consultation_status === 'contacted'
                      ? 'Contactat'
                      : profile.consultation_status === 'planned'
                      ? 'Programat'
                      : profile.consultation_status === 'closed'
                      ? 'Închis'
                      : profile.consultation_status === 'paid'
                      ? 'Plătit'
                      : 'Necunoscut'}
                  </span>
                </p>

                {profile.planned_date && (
                  <p className="text-gray-700">
                    Data programată: {formatDateTime(profile.planned_date)}
                  </p>
                )}

                {profile.meeting_url && (
                  <div className="mt-4">
                    {profile.consultation_status === 'closed' ? (
                      <div className="flex flex-col gap-4">
                        <button
                          onClick={handleNewConsultationRequest}
                          className="inline-flex items-center px-4 py-2 bg-[#024994] text-white rounded-lg hover:opacity-90 transition"
                        >
                          <Calendar className="w-5 h-5 mr-2" />
                          Solicită o nouă programare
                        </button>
                        <button
                          onClick={handleResendLetter}
                          disabled={resendingLetter}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <Mail className="w-5 h-5 mr-2" />
                          {resendingLetter
                            ? 'Se trimite...'
                            : 'Retrimite scrisoarea medicală pe e-mail'}
                        </button>
                      </div>
                    ) : (
                      <a
                        href={profile.meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-[#024994] text-white rounded-lg hover:opacity-90 transition"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Participă la consultație
                      </a>
                    )}
                  </div>
                )}
                {profile.consultation_status === 'closed' &&
                  !profile.meeting_url && (
                    <div className="flex flex-col gap-4">
                      <button
                        onClick={handleNewConsultationRequest}
                        className="inline-flex items-center px-4 py-2 bg-[#024994] text-white rounded-lg hover:opacity-90 transition"
                      >
                        <Calendar className="w-5 h-5 mr-2" />
                        Solicită o nouă programare
                      </button>
                      <button
                        onClick={handleResendLetter}
                        disabled={resendingLetter}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        {resendingLetter
                          ? 'Se trimite...'
                          : 'Retrimite scrisoarea medicală pe e-mail'}
                      </button>
                    </div>
                  )}

                {/* Request/Cancel Consultation Button */}
                <div className="mt-4">
                  {(profile.consultation_status === 'paid' ||
                    (profile.serviciu === 'probono' &&
                      profile.consultation_status !== 'requested')) && (
                    <button
                      onClick={handleRequestConsultation}
                      disabled={requestingConsultation}
                      className="px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition disabled:opacity-50"
                    >
                      {requestingConsultation
                        ? 'Se procesează...'
                        : 'Solicită programare'}
                    </button>
                  )}
                  {profile.consultation_status === 'planned' && (
                    <div className="space-y-2">
                      <button
                        onClick={handleCancelAppointment}
                        disabled={cancellingAppointment}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                      >
                        {cancellingAppointment
                          ? 'Se procesează...'
                          : 'Anulează programarea'}
                      </button>
                      {profile.planned_date && (
                        <p className="text-sm text-gray-600 italic">
                          Dacă din diverse motive nu poți să participi la
                          consultația online la data și ora stabilită, te rugăm
                          să anulezi programarea pe butonul de mai sus
                        </p>
                      )}
                    </div>
                  )}
                  {profile.consultation_status === 'requested' && (
                    <p className="text-sm text-gray-600 italic mt-4">
                      Echipa medicală a fost notificată. Vor verifica
                      documentele încărcate și le vor discuta într-o comisie
                      după care te vor programa pentru a discuta rezultatele cu
                      tine. Vei primi un e-mail cu data și ora la care vei fi
                      programat.
                    </p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Services Section */}
            {profile.serviciu !== 'probono' && (
              <motion.div
                className="bg-gray-50 p-6 rounded-lg"
                variants={fadeInUp}
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Servicii Disponibile
                </h2>
                <div className="space-y-4">
                  <div
                    className={`flex items-center justify-between p-4 rounded-lg shadow-sm transition-colors ${
                      (profile?.consultation_status === 'paid' ||
                        profile?.consultation_status === 'planned' ||
                        profile?.consultation_status === 'requested' ||
                        profile?.consultation_status === 'closed') &&
                      profile?.serviciu === 'standard'
                        ? 'bg-green-50 border-2 border-green-500'
                        : 'bg-white'
                    }`}
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Consultație standard
                      </h3>
                      <p className="text-gray-600">1000 lei</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleNetopiaPayment}
                        disabled={
                          standardPaymentLoading ||
                          !(
                            profile?.consultation_status === 'not_requested' ||
                            profile?.consultation_status === 'contacted'
                          )
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#27AE60] text-white rounded-lg hover:bg-[#219653] transition disabled:opacity-50 disabled:hover:bg-[#27AE60]"
                      >
                        {standardPaymentLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Se procesează...
                          </>
                        ) : (
                          'Spre plată'
                        )}
                      </button>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          placeholder="Cod recomandare"
                          className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-[#007FFF] focus:border-[#007FFF]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Documents Section */}
            <motion.div
              className="bg-gray-50 p-6 rounded-lg"
              variants={fadeInUp}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Documente Medicale
                  </h2>
                  <div className="flex gap-2">
                    <div>
                      {profile?.drive_link ? (
                        <a
                          href={profile.drive_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          <Link className="w-4 h-4" />
                          Accesează link Drive
                        </a>
                      ) : (
                        <button
                          onClick={handleGenerateUploadLink}
                          disabled={generatingLink}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <Link className="w-4 h-4" />
                          {generatingLink
                            ? 'Se generează...'
                            : 'Generează link Drive'}
                        </button>
                      )}
                      <div className="mt-2 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                        <p className="text-yellow-800 text-sm">
                          Documentele din drive se vor șterge după generarea scrisorii medicale din cauza lipsei de spațiu de stocare
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowUpload(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition"
                    >
                      <FileText className="w-4 h-4" />
                      Încarcă documente
                    </button>
                  </div>
                </div>

                <p className="text-gray-600 text-sm bg-blue-50 p-4 rounded-lg">
                  Asigurați-vă că încărcați toate documentele astfel încât
                  medicii să poată analiza cât mai precis istoricul medical.
                  Documentele cu dimensiuni mari de peste 50MB vă rugăm să le
                  încărcați în Drive (clic pe butonul verde "Generează link
                  drive").
                  <br />
                  <br />
                  Fișierele cu caractere speciale nu vor putea fi încărcate:
                  exemplu: semnul exclamării, acolade, paranteze, asterisc etc.
                </p>

                {files.length > 0 ? (
                  <div className="space-y-3">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between bg-white p-4 rounded-lg hover:bg-gray-100 group"
                      >
                        <div
                          className="flex items-center space-x-3 flex-1 cursor-pointer"
                          onClick={() => handleDownload(file.name)}
                        >
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-700">{file.name}</span>
                        </div>
                        {profile.consultation_status === 'not_requested' && (
                          <button
                            onClick={() => handleDeleteFile(file.name)}
                            className="text-red-600 hover:text-red-800 transition opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">Nu există documente încărcate</p>
                )}
              </div>
            </motion.div>

            {/* Notes Section */}
            <motion.div
              className="bg-gray-50 p-6 rounded-lg"
              variants={fadeInUp}
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Note pentru echipa medicală
              </h2>
              <div className="space-y-4">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Scrieți aici orice informații suplimentare doriți să transmiteți echipei medicale..."
                  className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#007FFF] focus:border-[#007FFF] min-h-[150px]"
                  rows={6}
                />
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="flex items-center gap-2 px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingNotes ? 'Se salvează...' : 'Salvează notele'}
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <DocumentUpload
              onClose={() => {
                setShowUpload(false);
                if (profile?.cnp) {
                  fetchFiles(profile.cnp);
                }
              }}
            />
          </motion.div>
        )}

        {paymentNotification && (
          <motion.div
            className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
              paymentNotification.type === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-green-100 text-green-800'
            }`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {paymentNotification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
